import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toggleAddon, reorderAddons, addAddon, updateAddon, deleteAddon } from '../actions';

const AddOnItem = ({ addon, index, currencySymbol, onEdit, onDelete, onToggle }) => (
  <Draggable draggableId={addon.id.toString()} index={index}>
    {(provided, snapshot) => (
      <tr ref={provided.innerRef} {...provided.draggableProps} className={snapshot.isDragging ? 'is-dragging' : ''}>
        <td {...provided.dragHandleProps} className="col-icon"><i className="fa fa-bars text-muted"></i></td>
        <td className="col-checkbox">
          <div className={`icheckbox_square-orange ${addon.active ? 'checked' : ''}`} onClick={onToggle}>
            <input type="checkbox" className="i-checks" checked={addon.active} readOnly style={{ opacity: 0 }} />
          </div>
        </td>
        <td>{addon.name}</td>
        <td>{currencySymbol}{parseFloat(addon.amount || 0).toFixed(2)}</td>
        <td>{addon.purchased || 0}</td>
        <td className="text-right">
          <button type="button" className="btn-link" onClick={onEdit}>Edit</button>
          <span className="text-muted"> • </span>
          <button type="button" className="btn-link text-danger" onClick={onDelete} disabled={addon.purchased > 0}>Delete</button>
        </td>
      </tr>
    )}
  </Draggable>
);

const AddOnForm = ({ initialData, metadatas, currencySymbol, onCancel, onSave }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [amount, setAmount] = useState(initialData?.amount ? parseFloat(initialData.amount).toFixed(2) : '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [metadatum_id, setMetadatumId] = useState(initialData?.metadatum_id || '');

  const handleNameChange = (e) => setName(e.target.value.trimStart().slice(0, 100));
  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) setAmount(val);
  };
  const handleDescriptionChange = (e) => setDescription(e.target.value.trimStart().slice(0, 500));

  return (
    <div className="add-margin-top-30">
      <h3 className="form-section-title">{initialData ? 'Edit Add-On' : 'New Add-On'}</h3>
      <form className="form-horizontal" onSubmit={(e) => { e.preventDefault(); onSave({ name, amount, description, metadatum_id }); }}>
        <div className="form-group">
          <label className="col-sm-3 control-label">Name</label>
          <div className="col-sm-9">
            <input type="text" className="form-control" value={name} onChange={handleNameChange} required maxLength={100} />
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label">Flat Fee ({currencySymbol})</label>
          <div className="col-sm-9">
            <input
              type="text"
              className="form-control"
              value={amount}
              onChange={handleAmountChange}
              placeholder="0.00"
              required
              maxLength={10}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label">Reporting Field</label>
          <div className="col-sm-9">
            <select className="form-control" value={metadatum_id} onChange={(e) => setMetadatumId(e.target.value)}>
              <option value="">None</option>
              {metadatas.map((m, i) => <option key={i} value={m[1]}>{m[0]}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label">Description</label>
          <div className="col-sm-9">
            <textarea className="form-control" rows={5} value={description} onChange={handleDescriptionChange} maxLength={500} />
          </div>
        </div>

        <div className="form-actions add-margin-top-20 clearfix">
          <div className="pull-right">
            <button type="button" className="btn btn-default btn-cancel" onClick={onCancel}>Cancel</button>
            <button type="submit" className="button btsp_orange_button">{initialData ? 'Update Add-On' : 'Create Add-On'}</button>
          </div>
        </div>
      </form>
    </div>
  );
};

const AddOnList = () => {
  const dispatch = useDispatch();
  const addons = useSelector(state => state.addons || []);
  const metadatas = useSelector(state => state.metadatas || []);
  const currencySymbol = useSelector(state => state.currency_symbol || '$');
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const visibleAddons = addons.filter(addon => !addon.deleted);
  const allChecked = visibleAddons.length > 0 && visibleAddons.every(addon => addon.active);

  const handleToggleAll = () => {
    const newState = !allChecked;
    visibleAddons.forEach(addon => {
      if (addon.active !== newState) {
        dispatch(toggleAddon(addon.id));
      }
    });
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const destroyed = addons.filter(a => a.deleted);
    const items = Array.from(visibleAddons);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    dispatch(reorderAddons([...items, ...destroyed]));
  };

  return (
    <div className="add-ons-container col-xs-12 col-sm-6 add-on-section-border">
      <div className="card-header clearfix">
        <div className="header-text pull-left">ADD-ONS</div>
        <button type="button" className="button btsp_orange_button pull-right" onClick={() => setIsFormVisible(true)}>
          New Add-On
        </button>
      </div>
      <div className="card-header-border" />

      <div className="add-ons-content">
        {visibleAddons.length === 0 ? (
          <div className="add-ons-empty-state">
            <p className="add-ons-empty-title"><strong>No add-ons in your list.</strong></p>
            <p>To create one, click the "New Add-On" button.</p>
          </div>
        ) : (
          <table className="table addon-table table-hover">
            <thead>
              <tr>
                <th className="col-icon"></th>
                <th className="col-checkbox">
                  <div className={`icheckbox_square-orange ${allChecked ? 'checked' : ''}`} onClick={handleToggleAll}>
                    <input type="checkbox" checked={allChecked} readOnly style={{ opacity: 0 }} />
                  </div>
                </th>
                <th>Add-on</th>
                <th>Price</th>
                <th>Total Quantity</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="addons">
                {(provided) => (
                  <tbody {...provided.droppableProps} ref={provided.innerRef}>
                    {visibleAddons.map((addon, index) => (
                      <AddOnItem
                        key={addon.id}
                        addon={addon}
                        index={index}
                        currencySymbol={currencySymbol}
                        onEdit={() => { setEditingAddon(addon); setIsFormVisible(true); }}
                        onDelete={() => dispatch(deleteAddon(addon.id))}
                        onToggle={() => dispatch(toggleAddon(addon.id))}
                      />
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
        )}

        {isFormVisible && (
          <AddOnForm
            initialData={editingAddon}
            metadatas={metadatas}
            currencySymbol={currencySymbol}
            onCancel={() => { setIsFormVisible(false); setEditingAddon(null); }}
            onSave={(formData) => {
              editingAddon ? dispatch(updateAddon({ ...editingAddon, ...formData })) : dispatch(addAddon(formData));
              setIsFormVisible(false);
              setEditingAddon(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AddOnList;